import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import { format } from "date-fns";
import { MessageSquare, Trash2, Loader2, User } from "lucide-react";

const CommentSection = ({ articleId }) => {
  const [comments, setComments] = useState([]);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user, login } = useAuth();

  useEffect(() => {
    fetchComments();
  }, [articleId]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/articles/${articleId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/articles/${articleId}/comments`,
        { article_id: articleId, content: content.trim() },
        { withCredentials: true }
      );
      setComments([response.data, ...comments]);
      setContent("");
      toast.success("Comment posted!");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Please sign in to comment");
      } else {
        toast.error("Failed to post comment");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`, { withCredentials: true });
      setComments(comments.filter((c) => c.comment_id !== commentId));
      toast.success("Comment deleted");
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  return (
    <section className="border-t border-border pt-12" data-testid="comment-section">
      <div className="flex items-center gap-3 mb-8">
        <MessageSquare className="h-5 w-5 text-primary" />
        <h3 className="font-heading font-bold text-xl uppercase tracking-tight">
          Comments ({comments.length})
        </h3>
      </div>

      {/* Comment Form */}
      {user ? (
        <form onSubmit={handleSubmit} className="mb-12">
          <div className="flex items-start gap-4">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <User className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1">
              <Textarea
                placeholder="Share your thoughts..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="bg-card border-border focus:border-primary rounded-none resize-none min-h-[100px]"
                data-testid="comment-input"
              />
              <div className="flex justify-end mt-4">
                <Button
                  type="submit"
                  disabled={submitting || !content.trim()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 font-mono uppercase tracking-widest text-xs"
                  data-testid="comment-submit"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Post Comment"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-12 p-6 bg-card border border-border">
          <p className="text-muted-foreground mb-4">Sign in to join the conversation.</p>
          <Button
            onClick={login}
            className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-10 px-6 font-mono uppercase tracking-widest text-xs"
            data-testid="comment-signin"
          >
            Sign In with Google
          </Button>
        </div>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">No comments yet. Be the first to share your thoughts.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {comments.map((comment) => (
            <div
              key={comment.comment_id}
              className="flex gap-4 pb-8 border-b border-border/50"
              data-testid={`comment-${comment.comment_id}`}
            >
              {comment.user_avatar ? (
                <img
                  src={comment.user_avatar}
                  alt={comment.user_name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{comment.user_name}</span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {format(new Date(comment.created_at), "MMM dd, yyyy")}
                    </span>
                  </div>
                  {user && (user.user_id === comment.user_id || user.role === "admin") && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(comment.comment_id)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      data-testid={`comment-delete-${comment.comment_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className="text-foreground/90 leading-relaxed">{comment.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default CommentSection;
